import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import { Formik } from 'formik'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import ScheduleReservation from 'services/ScheduleReservation'
import CheckBox from 'components/CheckBox'
import Select from 'components/Inputs/Select'
import DateInput from 'components/Inputs/DateInput'
import createReservationValidator from 'validators/createReservationValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import { DateTime } from 'luxon'

import {
  Form,
  FormField,
  Input,
  WeekdayContainer,
  Row,
  WhiteText,
  Label,
  WarningText,
  ErrorContainer
} from './styles'

export default function CreateReservationModal ({
  visible,
  timeInterval,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [weekdays, setWeekdays] = useState ([])
  const [businessHours, setBusinessHours] = useState (null)
  const [professionals, setProfessionals] = useState ([])
  const [startTimeWindows, setStartTimeWindows] = useState ([])
  const [endTimeWindows, setEndTimeWindows] = useState ([])
  const [errorMessage, setErrorMessage] = useState (null)
  const [loading, setLoading] = useState (true)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const { RECURRENCE_OPTIONS } = ScheduleReservation

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (visible) getData ()
  }, [visible])

  const getData = async () => {
    try {
      const [{ data: weekdays }, { data: workingDays }, { data: professionals }] = await Promise.all ([
        cutileiApi.get ('/weekdays'),
        cutileiApi.get (`/businesses/${businessId}/working_days`),
        cutileiApi.get (
          `/businesses/${businessId}/professionals?access_level=service&status=active&deleted=false`,
          requestConfig
        )
      ])

      const businessHours = TimeWindow.getBusinessHours(workingDays)

      setStartTimeWindows (
        TimeWindow.getTimeWindows (businessHours?.opening, businessHours?.closing, false, timeInterval)
      )
      setEndTimeWindows (
        TimeWindow.getTimeWindows (businessHours?.opening, businessHours?.closing, false, timeInterval)
      )

      setWeekdays (weekdays)
      setBusinessHours (businessHours)
      setProfessionals ([
        {
          label: 'Todos',
          value: 'all_professionals'
        },
        ...professionals.map (professional => ({
          label: professional.nickname,
          value: professional
        }))
      ])
    } catch (error) {
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Reservar agenda</DialogTitle>
        </DialogHeader>
        {loading ? <DialogBody><Loading/></DialogBody> : (
          <Formik
            validationSchema={createReservationValidator}
            initialValues={{
              'professionals': [],
              'description': '',
              'startDate': '',
              'endDate': '',
              'period': null,
              'startTime': null,
              'endTime': null,
              'recurrence': RECURRENCE_OPTIONS[0],
              'weekdays': weekdays.map (day => ({...day, selected: false}))
            }}
            onSubmit={async (values, { setSubmitting }) => {
              const {
                professionals,
                description,
                startDate,
                endDate,
                startTime,
                endTime,
                recurrence,
                weekdays
              } = values

              const ReservationData = {
                description,
                startDate,
                endDate,
                startTime: startTime.value,
                endTime: endTime.value,
                recurrenceType: recurrence.value.recurrenceType,
                recurrenceInterval: recurrence.value.recurrenceInterval,
                weekdays: weekdays.filter (day => day.selected)
              }

              try {
                setErrorMessage (null)
                const isAllProfessionals = professionals.some (p => p.value === 'all_professionals')
                let reservations = []

                if (isAllProfessionals) {
                  const { data } = await cutileiApi.post (`/businesses/${businessId}/schedule_reservations`, {
                    ...ReservationData,
                    professionalId: 'all_professionals'
                  }, requestConfig)

                  reservations = [...data]
                } else {
                  for (let i = 0; i < professionals.length; i++) {
                    const { data } = await cutileiApi.post (`/businesses/${businessId}/schedule_reservations`, {
                      ...ReservationData,
                      professionalId: professionals[i].value.id
                    }, requestConfig)
                    reservations.push (data[0])
                  }
                }

                setSubmitting (false)
                handleConfirm (reservations)
                handleClose ()
              } catch (error) {
                setSubmitting (false)
                setErrorMessage (getApiErrorMessage (error))
                logApiError (error)
              }
            }}
          >
            {function CreateReservationForm ({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              handleChange,
              handleBlur,
              handleSubmit
            }) {
              useEffect (() => {
                if (values.startDate && values.period)
                  handlePeriodChange ('period', values.period)
              }, [values.startDate])

              const handlePeriodChange = (name, option) => {
                let interval = null
                const { weekdays } = values
                const startDate = DateTime.fromISO (values.startDate)

                switch (option.value) {
                  case -2: interval = null; break // Personalizado
                  case -1: interval = {years: 500}; break // Indeterminado
                  case 30: interval = {months: 1}; break // 1 mês
                  default: interval = {days: option.value} // 1 dia - 7 dias - 15 dias
                }

                const endDate = startDate.plus (interval ?? {days: 1}).toISODate ()
                setFieldValue ('endDate', endDate)

                if (option.value !== -1) handleSelectAllDays (option.value <= 15, weekdays)
                if (option.value === 0) setFieldValue ('weekdays', weekdays.map (day => ({
                  ...day, selected: day.iso_number === startDate.weekday
                })))

                setFieldValue (name, option)
              }

              const handleStartDateSelected = startDate => {
                if (values.period?.value === 0)
                  setFieldValue ('weekdays', weekdays.map (day => ({
                    ...day, selected: day.iso_number === DateTime.fromISO (startDate).weekday
                  })))
                setFieldValue ('startDate', startDate)
              }

              const handleEndDateSelected = endDate => {
                const { startDate } = values
                const dateInterval = { startDate, endDate }

                handleSelectAllDays (getPeriodInDays (dateInterval) <= 15, weekdays)
                setFieldValue ('endDate', endDate)
              }

              const handleTimeSelected = (name, option) => {
                if (name === 'startTime') {
                  setEndTimeWindows (TimeWindow.getTimeWindows (option.value, businessHours?.closing, true))
                } else if (name === 'endTime') {
                  setStartTimeWindows (TimeWindow.getTimeWindows (businessHours?.opening, option.value, true))
                }
                setFieldValue (name, option)
              }

              const handleSelectAllDays = (value, weekdays) => {
                setFieldValue ('weekdays', weekdays.map (day => ({ ...day, selected: value })))
              }

              const getPeriodInDays = values => {
                const startDate = DateTime.fromISO (values.startDate)
                const endDate = DateTime.fromISO (values.endDate)
                const periodInDays = endDate.diff (startDate, 'days').toObject ().days

                return periodInDays
              }
            
              const selectAllDays = useMemo (() => (
                values.weekdays.filter (day => day.selected).length === 7
              ), [values.weekdays])
            
              const periodInDays = useMemo (() => getPeriodInDays (values), [values])
              const showRecurrence = useMemo (() => periodInDays >= 30, [periodInDays])

              useEffect (() => {
                if (!showRecurrence) setFieldValue ('recurrence', RECURRENCE_OPTIONS[0])
              }, [showRecurrence, setFieldValue])
            
              return (
                <>
                  <DialogBody>
                    <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                      <Row>
                        <FormField>
                          <Select
                            id='professionals'
                            aria-label='Profissionais'
                            name='professionals'
                            placeholder='Profissionais...'
                            value={values.professionals}
                            options={professionals}
                            isMulti={true}
                            isClearable={true}
                            onRemoveOption={professional => {
                              setFieldValue (
                                'professionals', values.professionals.filter (p => p.value.id !== professional.value.id)
                              )
                            }}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            error={errors.professionals}
                            touched={touched.professionals}
                          />
                          {touched.professionals && errors.professionals && (
                            <WarningText>
                              {errors.professionals}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <Input 
                            placeholder='Descrição da reserva'
                            value={values.description}
                            onChange={handleChange ('description')} 
                            onBlur={handleBlur ('description')}
                          />
                          {touched.description && errors.description && (
                            <WarningText>
                              {errors.description}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      <Row>
                        <FormField>
                          <DateInput
                            placeholder='Data de início'
                            value={values.startDate}
                            onChange={handleStartDateSelected}
                            style={{width: '100%'}}
                          />
                          {touched.startDate && errors.startDate && (
                            <WarningText>
                              {errors.startDate}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField style={{marginLeft: 5, marginRight: values.period?.value === -2 ? 5 : 0}}>
                          <Select
                            name='period'
                            placeholder='Período...'
                            value={values.period}
                            options={ScheduleReservation.PERIODS}
                            onChange={handlePeriodChange}
                            onBlur={setFieldTouched}
                            error={errors.period}
                            touched={touched.period}
                          />
                          {touched.period && errors.period && (
                            <WarningText>
                              {errors.period}
                            </WarningText>
                          )}
                        </FormField>
                        {values.period?.value === -2 &&
                          <FormField>
                            <DateInput
                              placeholder='Data final'
                              value={values.endDate}
                              onChange={handleEndDateSelected}
                              style={{width: '100%'}}
                            />
                            {touched.endDate && errors.endDate && (
                              <WarningText>
                                {errors.endDate}
                              </WarningText>
                            )}
                          </FormField>
                        }
                      </Row>
                      {values.professionals.length > 0 && (
                        <Row>
                          <FormField>
                            <Select
                              name='startTime'
                              placeholder='Horário inicial...'
                              value={values.startTime}
                              options={startTimeWindows}
                              onChange={handleTimeSelected}
                              onBlur={setFieldTouched}
                              error={errors.startTime}
                              touched={touched.startTime}
                            />
                            {touched.startTime && errors.startTime && (
                              <WarningText>
                                {errors.startTime}
                              </WarningText>
                            )}
                          </FormField>
                          <FormField>
                            <Select
                              name='endTime'
                              placeholder='Horário final...'
                              value={values.endTime}
                              options={endTimeWindows}
                              onChange={handleTimeSelected}
                              onBlur={setFieldTouched}
                              error={errors.endTime}
                              touched={touched.endTime}
                            />
                            {touched.endTime && errors.endTime && (
                              <WarningText>
                                {errors.endTime}
                              </WarningText>
                            )}
                          </FormField>
                        </Row>
                      )}
                      {periodInDays > 15 && (
                        <FormField style={{marginBottom: 10}}>
                          <Label style={{marginBottom: 5}}>
                            Selecione os dias da semana em que deseja repetir esta reserva
                          </Label>
                          <WeekdayContainer>
                            {weekdays.map ((day, index) => (
                              <Row key={day.id} style={{alignItems: 'center', margin: 0}}>
                                <CheckBox
                                  id={`weekdays.${index}.selected`}
                                  style={{marginRight: 6}}
                                  value={values.weekdays[index].selected}
                                  onValueChange={value => setFieldValue (`weekdays.${index}.selected`, value)}
                                />
                                <Label htmlFor={`weekdays.${index}.selected`}>
                                  {day.name.substring (0, 3)}
                                </Label>
                              </Row>
                            ))}
                          </WeekdayContainer>
                          <Row style={{alignItems: 'center', marginBottom: 4}}>
                            <CheckBox
                              id='select_all'
                              style={{marginRight: 6}}
                              value={selectAllDays}
                              onValueChange={value => handleSelectAllDays (value, values.weekdays)}
                            />
                            <Label htmlFor='select_all'>
                              Selecionar todos os dias
                            </Label>
                          </Row>
                          {touched.weekdays && errors.weekdays && (
                            <WarningText>
                              {errors.weekdays}
                            </WarningText>
                          )}
                        </FormField>
                      )}
                      {showRecurrence && (
                        <Row>
                          <FormField>
                            <Label htmlFor='recurrence' style={{marginBottom: 5}}>
                              Recorrência
                            </Label>
                            <Select
                              id='recurrence'
                              name='recurrence'
                              placeholder='Recorrência...'
                              value={values.recurrence}
                              options={RECURRENCE_OPTIONS}
                              onChange={setFieldValue}
                              onBlur={setFieldTouched}
                              containerStyles={{width: 250}}
                            />
                          </FormField>
                        </Row>
                      )}
                      {errorMessage && (
                        <ErrorContainer>
                          <WhiteText>{errorMessage}</WhiteText>
                        </ErrorContainer>
                      )}
                    </Form>
                  </DialogBody>
                  <DialogFooter>
                    <MainButton
                      label='Criar reserva'
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={180}
                    />
                  </DialogFooter>
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
